import React, { useContext, useEffect, useState } from 'react';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ClearRoundedIcon from '@material-ui/icons/ClearRounded';
import AddIcon from '@material-ui/icons/Add';
import { Collapse, Grid, IconButton, styled } from '@material-ui/core';
import { SdmTypography, SdmSpacer } from 'components';
import SlotCard from './SlotCard';
import { intlFormat } from 'date-fns';
import { useDataHelper } from 'utils/dataHelper';
import { theme } from 'theme';
import { VisitsContext } from 'context/VisitsContextProvider';

const ActionLink = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  cursor: 'pointer',
});

const AppointmentCard = ({
  appointment,
  alreadyOpen = false,
  isSmDown,
  isXsDown,
  i18n,
  locale,
  onViewSlot,
  onDeleteSlot,
  onMultipleDeleteSlot,
  onAddMorning,
  onAddAfternoon,
}) => {
  // Hooks
  const dataHelper = useDataHelper();
  const { selectedSlots, setSelectedSlots } = useContext(VisitsContext);
  // States
  const [collapsableCtrl, setCollapsableCtrl] = useState(false);
  const [morningSlots, setMorningSlots] = useState([]);
  const [afternoonSlots, setAfternoonSlots] = useState([]);
  const [isMultipleSelectionActive, setIsMultipleSelectionActive] =
    useState(false);
  // Effects
  useEffect(() => {
    if (alreadyOpen) setCollapsableCtrl(true);
  }, [alreadyOpen]);

  useEffect(() => {
    if (appointment && appointment.slots?.items?.length) {
      const morning = [];
      const afternoon = [];
      appointment.slots?.items
        .sort((a, b) => new Date(a.start) - new Date(b.start))
        .map((s) => {
          morning.push(s);
        });
      setMorningSlots(morning);
      setAfternoonSlots(afternoon);
    }
  }, []);

  useEffect(() => {
    setIsMultipleSelectionActive(
      selectedSlots &&
        selectedSlots.length &&
        !!appointment.slots.items?.find((slot) => slot.id === selectedSlots[0])
    );
  }, [selectedSlots]);
  // Functions
  const handleExpandClick = () => {
    setCollapsableCtrl(!collapsableCtrl);
    setSelectedSlots([]);
  };

  const handleMorningAdd = () => onAddMorning?.(appointment);

  const handleAfternoonAdd = () => onAddAfternoon?.(appointment);

  const handleView = (id) => onViewSlot && onViewSlot(id);

  const handleDelete = (id) => onDeleteSlot && onDeleteSlot(id, appointment);

  const handleMultipleDelete = () =>
    onMultipleDeleteSlot && onMultipleDeleteSlot(selectedSlots);

  // Constants
  const date = intlFormat(
    new Date(appointment?.date),
    {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    },
    {
      locale: dataHelper.getLangCode(locale),
    }
  );
  return (
    <>
      <div
        style={{
          borderBottom: '1px solid #979797',
          width: '100%',
          paddingBottom: 10,
          marginTop: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <SdmTypography margin={0} variant={isSmDown ? 'h5' : 'h1'}>
          {date}
        </SdmTypography>
        <IconButton
          disableFocusRipple
          disableTouchRipple
          disableRipple
          onClick={() => handleExpandClick()}
        >
          {collapsableCtrl ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </div>
      <SdmSpacer height={20} />
      <Grid container>
        <Collapse in={collapsableCtrl} style={{ width: '100%' }}>
          {morningSlots.length ? (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <ActionLink
                  onClick={() =>
                    isMultipleSelectionActive
                      ? handleMultipleDelete()
                      : handleMorningAdd()
                  }
                >
                  {isMultipleSelectionActive ? (
                    <ClearRoundedIcon
                      style={{
                        color: theme.colors.primary.red,
                        marginRight: 8,
                      }}
                    />
                  ) : (
                    <AddIcon
                      style={{
                        color: theme.colors.primary.red,
                        marginRight: 8,
                      }}
                    />
                  )}
                  <SdmTypography
                    variant={'h4Regular'}
                    margin={0}
                    style={{
                      color: theme.colors.primary.red,
                      marginTop: 3,
                    }}
                  >
                    {isMultipleSelectionActive ? i18n.removeSelected : i18n.add}
                  </SdmTypography>
                </ActionLink>
              </div>
              <SdmSpacer height={20} />
              {morningSlots.map((s, idx) => (
                <SlotCard
                  key={idx}
                  slot={s}
                  isSmDown={isSmDown}
                  isXsDown={isXsDown}
                  i18n={i18n}
                  onView={handleView}
                  onDelete={handleDelete}
                />
              ))}
            </>
          ) : null}
          <SdmSpacer height={isSmDown ? 20 : 40} />
          {afternoonSlots.length ? (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <SdmTypography
                  margin={0}
                  variant={isSmDown ? 'h5Regular' : 'h1Regular'}
                  style={{ textTransform: 'uppercase' }}
                >
                  {i18n.pm}
                </SdmTypography>
                <ActionLink onClick={() => handleAfternoonAdd()}>
                  <AddIcon
                    style={{
                      color: theme.colors.primary.red,
                      marginRight: 8,
                    }}
                  />
                  <SdmTypography
                    variant={'h4Regular'}
                    margin={0}
                    style={{
                      color: theme.colors.primary.red,
                      marginTop: 3,
                    }}
                  >
                    {i18n.add}
                  </SdmTypography>
                </ActionLink>
              </div>
              <SdmSpacer height={20} />
              {afternoonSlots.map((s, idx) => (
                <SlotCard
                  key={idx}
                  slot={s}
                  isSmDown={isSmDown}
                  isXsDown={isXsDown}
                  i18n={i18n}
                  onView={handleView}
                  onDelete={handleDelete}
                />
              ))}
            </>
          ) : null}
        </Collapse>
        <SdmSpacer height={40} />
      </Grid>
    </>
  );
};
export default AppointmentCard;
